import React, { useEffect, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import { Box, Button, CircularProgress } from '@mui/material';
import { environment } from '../../environments/environment';
import { useAuth } from 'oidc-react';

interface IProps extends RouteComponentProps {
  history: any;
  location: any;
  match: any;
  config: any;
}

// Separate wrapper so useAuth() is only called when inside <AuthProvider> (OIDC mode).
// In Okta mode, OktaLoginComponent renders outside <AuthProvider>, so calling
// useAuth() at the top level crashes with "AuthProvider context is undefined".
const OIDCLoginWrapper: React.FC = () => {
  const oidcAuth = useAuth() as any;
  return <OIDCLoginContent oidcAuth={oidcAuth} />;
};

const OktaLoginComponent: React.FC<IProps> = ({
  history,
  location,
  match,
  config
}) => {
  if (environment.oidc.isEnabled) {
    return <OIDCLoginWrapper />;
  }

  if (environment.okta.isEnabled) {
    return <OKTALoginContent />;
  }

  return (
    <Box sx={{ padding: '2rem', textAlign: 'center' }}>
      <p>No authentication mode is enabled.</p>
      <p>Please configure OKTA or OIDC in your environment settings.</p>
    </Box>
  );
};

interface OIDCLoginContentProps {
  oidcAuth: any;
}

const OIDCLoginContent: React.FC<OIDCLoginContentProps> = ({ oidcAuth }) => {
  const hasTriggeredSignIn = useRef(false);
  const [showSignInButton, setShowSignInButton] = useState(false);

  useEffect(() => {
    // If user intentionally logged out, show login button — do NOT auto-redirect
    const isLoggedOut = sessionStorage.getItem('userLoggedOut') === 'true';
    if (isLoggedOut) {
      setShowSignInButton(true);
      return;
    }

    // Check if we have a valid token already
    const accessToken = localStorage.getItem('access_token');
    const tokenExpiry = sessionStorage.getItem('tokenExpiry');
    if (accessToken && tokenExpiry && new Date().getTime() < parseInt(tokenExpiry, 10)) {
      console.log('OIDC Login: Valid cache found, redirecting to home');
      window.location.href = '/';
      return;
    }

    // If OIDC auth is ready and we have a token, redirect to home
    if (oidcAuth?.userData?.access_token) {
      console.log('OIDC Login: Auth ready with token, redirecting to home');
      window.location.href = '/';
      return;
    }

    // Auto-redirect to Azure AD only on first visit (not after logout)
    if (!oidcAuth?.isLoading && !hasTriggeredSignIn.current) {
      hasTriggeredSignIn.current = true;
      console.log('OIDC Login: No session found, redirecting to Azure AD...');
      oidcAuth.signIn().catch((err: any) => {
        console.error('OIDC signIn failed:', err);
        hasTriggeredSignIn.current = false; // allow retry on next render
      });
    }
  }, [oidcAuth?.userData?.access_token, oidcAuth?.isLoading]);

  const handleSignIn = () => {
    sessionStorage.removeItem('userLoggedOut');
    // prompt: 'login' forces Azure AD to show the login form even if SSO session is active
    oidcAuth.signIn({ prompt: 'login' }).catch((err: any) => {
      console.error('OIDC signIn failed:', err);
    });
  };

  if (showSignInButton) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          gap: 2,
          backgroundColor: 'var(--color-surface-muted)'
        }}
      >
        <img src="/atman-logo.svg" alt="Atman Health" style={{ height: '60px', marginBottom: '1rem' }} onError={(e: any) => { e.target.style.display = 'none'; }} />
        <p style={{ fontSize: '1.1rem', fontWeight: 500 }}>You have been signed out</p>
        <Button variant="contained" onClick={handleSignIn} sx={{ mt: 1 }}>
          Sign In
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        gap: 2,
        backgroundColor: 'var(--color-surface-muted)'
      }}
    >
      <CircularProgress />
      <p>Initializing OIDC authentication...</p>
      <p style={{ fontSize: '0.875rem', color: '#666' }}>
        You will be redirected to the login provider
      </p>
    </Box>
  );
};

// Okta-hosted login redirect — no embedded widget.
// The authStateManager in App.tsx handles the unauthenticated → signInWithRedirect()
// flow for all protected routes. This component covers the case where the user
// navigates directly to /login: redirect to Okta if not authenticated, or home if they are.
const OKTALoginContent: React.FC = () => {
  const { authState, oktaAuth } = useOktaAuth()

  useEffect(() => {
    if (!authState || authState.isPending) return

    if (authState.isAuthenticated) {
      // Already logged in — send to landing page
      window.location.href = '/'
    } else {
      // Not authenticated — go to Okta-hosted login page
      oktaAuth.signInWithRedirect({ redirectUri: window.location.origin + '/login/callback' })
    }
  }, [authState?.isAuthenticated, authState?.isPending])

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <CircularProgress />
    </Box>
  )
};

export default OktaLoginComponent;